import Tooltip from 'antd/es/tooltip'
import { RcFile } from 'antd/es/upload'
import axios from 'axios'
import Modules from 'application/modules'
import { Dispatch } from 'application/redux'
import AccessControlledComponent from 'contexts/ACL/ControlledComponent'
import useI18n from 'contexts/I18n/hook'
import AuthService from 'core/auth/auth-service'
import { Roles } from 'core/auth/constants'
import { addToaster } from 'core/toaster/redux/actions'
import { ToasterType } from 'core/toaster/redux/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createPlatform,
  deleteBuild,
  fetchInitConfiguration,
  fetchPlatforms,
  fetchVersions,
  saveInitConfiguration,
  uploadBuild,
} from '../services/bootstrap-service'
import { BootstrapBuild, BootstrapConfiguration, BootstrapInit, BootstrapVersion, BootstrapVersionId } from '../types'
import { backendErrorMessage, buildRecoveryUrl, translate } from '../utils'
import BuildConfiguration from './BuildConfiguration'
import DeleteBuildModal from './DeleteBuildModal'
import NewPlatformModal from './NewPlatformModal'

type ConfigurationsByVersion = Record<string, Record<string, BootstrapConfiguration>>
type PlatformsByVersion = Record<string, Array<string>>

function createDefaultConfiguration(versionId: BootstrapVersionId): BootstrapConfiguration {
  return {
    init: {
      version: versionId === 'v2' ? 3 : 2,
      minVersion: '',
      referencedBuild: '',
    },
    builds: [],
  }
}

export default function BootstrapManagement() {
  const t = useI18n()
  const dispatch = useDispatch() as Dispatch
  const [versions, setVersions] = useState<Array<BootstrapVersion>>([])
  const [platformsByVersion, setPlatformsByVersion] = useState<PlatformsByVersion>({})
  const [configurations, setConfigurations] = useState<ConfigurationsByVersion>({})
  const [selectedVersion, setSelectedVersion] = useState<BootstrapVersionId | ''>('')
  const [selectedPlatform, setSelectedPlatform] = useState('')
  const [initDraft, setInitDraft] = useState<BootstrapInit>()
  const [isNewPlatformModalOpen, setIsNewPlatformModalOpen] = useState(false)
  const [buildToDelete, setBuildToDelete] = useState<BootstrapBuild>()
  const [uploadingFileName, setUploadingFileName] = useState<string>()
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingVersions, setIsLoadingVersions] = useState(false)
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(false)
  const [isLoadingInit, setIsLoadingInit] = useState(false)
  const latestInitRequest = useRef(0)
  const uploadAbort = useRef<AbortController | undefined>(undefined)
  const canEdit = useMemo(() => AuthService.isSuperUser(Modules.bootstrap), [])

  const notifyError = useCallback(
    (error?: unknown) => {
      dispatch(
        addToaster({
          title: 'home.icon_bootstrap',
          message: backendErrorMessage(error),
          type: ToasterType.danger,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    let isSubscribed = true
    setIsLoadingVersions(true)

    fetchVersions()
      .then((result) => {
        if (isSubscribed) {
          setVersions(result)
        }
      })
      .catch((error) => notifyError(error))
      .finally(() => {
        if (isSubscribed) {
          setIsLoadingVersions(false)
        }
      })

    return () => {
      isSubscribed = false
    }
  }, [notifyError])

  const platforms = useMemo(
    () => (selectedVersion ? platformsByVersion[selectedVersion] || [] : []),
    [platformsByVersion, selectedVersion]
  )

  const configuration = useMemo(
    () =>
      selectedVersion && selectedPlatform
        ? configurations[selectedVersion]?.[selectedPlatform]
        : undefined,
    [configurations, selectedPlatform, selectedVersion]
  )
  const builds = configuration?.builds || []
  const hasVersionAndPlatform = Boolean(selectedVersion && selectedPlatform && initDraft)
  const draftBuild = initDraft?.referencedBuild || ''
  const savedBuild = configuration?.init?.referencedBuild || ''
  const isInitValid = Boolean(
    selectedVersion &&
      initDraft?.minVersion?.trim() &&
      initDraft.recoveryUrl?.trim() &&
      draftBuild &&
      (selectedVersion !== 'v2' || initDraft.ccUrl?.trim())
  )

  const handleUploadBuild = async (file?: RcFile) => {
    if (!file || !selectedVersion || !selectedPlatform) {
      return
    }

    setUploadingFileName(file.name)
    setUploadProgress(0)
    const controller = new AbortController()
    uploadAbort.current = controller

    try {
      await uploadBuild(selectedVersion, selectedPlatform, file, setUploadProgress, controller.signal)
      const refreshedConfiguration = await fetchInitConfiguration(selectedVersion, selectedPlatform)

      setConfigurations((currentConfigurations) => {
        const currentConfiguration =
          currentConfigurations[selectedVersion]?.[selectedPlatform] ||
          createDefaultConfiguration(selectedVersion)

        return {
          ...currentConfigurations,
          [selectedVersion]: {
            ...currentConfigurations[selectedVersion],
            [selectedPlatform]: {
              ...currentConfiguration,
              builds: refreshedConfiguration.builds,
            },
          },
        }
      })
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      if ((error as any)?.response?.status === 409) {
        dispatch(
          addToaster({
            title: 'home.icon_bootstrap',
            message: 'bootstrap.build_already_exists',
            type: ToasterType.danger,
          })
        )
      } else {
        notifyError(error)
      }
    } finally {
      uploadAbort.current = undefined
      setUploadingFileName(undefined)
    }
  }

  const handleCancelUpload = () => uploadAbort.current?.abort()

  const handleVersionChange = async (versionId: string) => {
    const nextVersion = versionId as BootstrapVersionId | ''

    latestInitRequest.current += 1
    setSelectedVersion(nextVersion)
    setSelectedPlatform('')
    setInitDraft(undefined)
    setIsDirty(false)
    setIsLoadingInit(false)

    if (!nextVersion) {
      return
    }

    setIsLoadingPlatforms(true)
    try {
      const result = await fetchPlatforms(nextVersion)
      setPlatformsByVersion((currentPlatforms) => ({
        ...currentPlatforms,
        [nextVersion]: result,
      }))
    } catch (error) {
      notifyError(error)
    } finally {
      setIsLoadingPlatforms(false)
    }
  }

  const handlePlatformChange = async (platform: string) => {
    const requestId = latestInitRequest.current + 1
    latestInitRequest.current = requestId

    setSelectedPlatform(platform)
    setInitDraft(undefined)
    setIsDirty(false)
    setIsLoadingInit(false)

    if (!selectedVersion || !platform) {
      return
    }

    const cachedConfiguration = configurations[selectedVersion]?.[platform]
    if (cachedConfiguration) {
      setInitDraft(cachedConfiguration.init)
      return
    }

    setIsLoadingInit(true)
    try {
      const result = await fetchInitConfiguration(selectedVersion, platform)

      if (latestInitRequest.current !== requestId) {
        return
      }

      const configurationToShow = result.init
        ? result
        : { ...result, init: createDefaultConfiguration(selectedVersion).init }

      setConfigurations((currentConfigurations) => ({
        ...currentConfigurations,
        [selectedVersion]: {
          ...currentConfigurations[selectedVersion],
          [platform]: configurationToShow,
        },
      }))
      setInitDraft(configurationToShow.init)
    } catch (error) {
      if (latestInitRequest.current === requestId) {
        notifyError(error)
      }
    } finally {
      if (latestInitRequest.current === requestId) {
        setIsLoadingInit(false)
      }
    }
  }

  const handleNewPlatform = async (platformName: string) => {
    if (!selectedVersion) {
      return
    }

    try {
      await createPlatform(selectedVersion, platformName)
      setIsNewPlatformModalOpen(false)

      const result = await fetchPlatforms(selectedVersion)
      setPlatformsByVersion((currentPlatforms) => ({
        ...currentPlatforms,
        [selectedVersion]: result,
      }))
      await handlePlatformChange(platformName)
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        dispatch(
          addToaster({
            title: 'home.icon_bootstrap',
            message: 'bootstrap.platform_already_exists',
            type: ToasterType.danger,
          })
        )
      } else {
        notifyError(error)
      }
    }
  }

  const handleInitFieldChange = (
    fieldName: 'ccUrl' | 'minVersion' | 'recoveryUrl',
    value: string
  ) => {
    setInitDraft((currentInitDraft) => {
      if (!currentInitDraft) {
        return currentInitDraft
      }

      return {
        ...currentInitDraft,
        [fieldName]: value,
      }
    })
    setIsDirty(true)
  }

  const handleBuildReferenceChange = (buildName: string) => {
    setInitDraft((currentInitDraft) => {
      if (!currentInitDraft || !selectedVersion) {
        return currentInitDraft
      }

      return {
        ...currentInitDraft,
        referencedBuild: buildName,
        recoveryUrl: buildName
          ? buildRecoveryUrl(
              selectedVersion,
              selectedPlatform,
              buildName,
              currentInitDraft.recoveryUrl
            )
          : '',
      }
    })
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!selectedVersion || !selectedPlatform || !initDraft || !isInitValid) {
      return
    }

    setIsSaving(true)
    try {
      await saveInitConfiguration(selectedVersion, selectedPlatform, initDraft)
      const refreshedConfiguration = await fetchInitConfiguration(selectedVersion, selectedPlatform)

      setConfigurations((currentConfigurations) => ({
        ...currentConfigurations,
        [selectedVersion]: {
          ...currentConfigurations[selectedVersion],
          [selectedPlatform]: refreshedConfiguration,
        },
      }))
      setInitDraft(refreshedConfiguration.init)
      setIsDirty(false)
      dispatch(
        addToaster({
          title: 'home.icon_bootstrap',
          message: 'bootstrap.init_saved',
          type: ToasterType.success,
        })
      )
    } catch (error) {
      notifyError(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteBuild = async () => {
    const fileName = buildToDelete?.fileName

    if (!selectedVersion || !selectedPlatform || !fileName || fileName === savedBuild) {
      return
    }

    setBuildToDelete(undefined)

    try {
      await deleteBuild(selectedVersion, selectedPlatform, fileName)
      const refreshedConfiguration = await fetchInitConfiguration(selectedVersion, selectedPlatform)

      setConfigurations((currentConfigurations) => {
        const currentConfiguration =
          currentConfigurations[selectedVersion]?.[selectedPlatform] ||
          createDefaultConfiguration(selectedVersion)

        return {
          ...currentConfigurations,
          [selectedVersion]: {
            ...currentConfigurations[selectedVersion],
            [selectedPlatform]: {
              ...currentConfiguration,
              builds: refreshedConfiguration.builds,
            },
          },
        }
      })

      if (fileName === draftBuild) {
        handleBuildReferenceChange('')
      }

      dispatch(
        addToaster({
          title: 'home.icon_bootstrap',
          message: 'bootstrap.build_deleted',
          type: ToasterType.success,
        })
      )
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <>
      <div className="bootstrap-selectors">
        <div className="bootstrap-selector-column">
          <label className="label bootstrap-label" htmlFor="bootstrap-version">
            {translate(t, 'bootstrap.version_label')}
          </label>
          <div className="select is-fullwidth">
            <select
              id="bootstrap-version"
              value={selectedVersion}
              disabled={isLoadingVersions}
              onChange={(event) => handleVersionChange(event.target.value)}
            >
              <option value="">{translate(t, 'bootstrap.version_placeholder')}</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bootstrap-selector-separator" />

        <div className="bootstrap-selector-column is-platform">
          <label className="label bootstrap-label" htmlFor="bootstrap-platform">
            {translate(t, 'bootstrap.platforms_label')}
          </label>
          <div className="bootstrap-platform-row">
            <div className="select is-fullwidth">
              <select
                id="bootstrap-platform"
                value={selectedPlatform}
                disabled={!selectedVersion || isLoadingPlatforms}
                onChange={(event) => handlePlatformChange(event.target.value)}
              >
                <option value="">{translate(t, 'bootstrap.platform_placeholder')}</option>
                {platforms.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>
            <AccessControlledComponent module={Modules.bootstrap} role={Roles.SU}>
              <button
                type="button"
                className="button is-primary bootstrap-create-button"
                disabled={!selectedVersion || isLoadingPlatforms}
                onClick={() => setIsNewPlatformModalOpen(true)}
              >
                {translate(t, 'bootstrap.create_platform')}
              </button>
            </AccessControlledComponent>
          </div>
        </div>
      </div>

      <section className="bootstrap-section">
        <h4 className="bootstrap-section-title">
          {translate(t, 'bootstrap.init_configuration_title')}
        </h4>

        {!hasVersionAndPlatform && !isLoadingInit && (
          <p className="bootstrap-helper-text">
            {translate(t, 'bootstrap.select_version_platform_for_init')}
          </p>
        )}

        {hasVersionAndPlatform && initDraft && (
          <div className="bootstrap-init-form">
            <div className="bootstrap-field is-wide">
              <span>{translate(t, 'bootstrap.field_recovery_url')}</span>
              <Tooltip title={initDraft.recoveryUrl || undefined}>
                <input
                  className={`input ${
                    !canEdit || !draftBuild || initDraft.recoveryUrl?.trim() ? '' : 'is-danger'
                  }`}
                  value={initDraft.recoveryUrl || ''}
                  disabled={!canEdit || !draftBuild}
                  onChange={(event) =>
                    handleInitFieldChange('recoveryUrl', event.target.value)
                  }
                />
              </Tooltip>
            </div>
            <div className="bootstrap-init-fields-row">
              <div className="bootstrap-field is-small">
                <span>{translate(t, 'bootstrap.field_version')}</span>
                <input
                  className="input"
                  value={
                    typeof initDraft.version === 'number'
                      ? `v${initDraft.version}`
                      : selectedVersion
                  }
                  disabled
                />
              </div>
              <div className="bootstrap-field is-minimum-version">
                <span>{translate(t, 'bootstrap.field_minimum_version')}</span>
                <input
                  className={`input ${!canEdit || initDraft.minVersion?.trim() ? '' : 'is-danger'}`}
                  value={initDraft.minVersion || ''}
                  disabled={!canEdit}
                  onChange={(event) => handleInitFieldChange('minVersion', event.target.value)}
                />
              </div>
              {selectedVersion === 'v2' && (
                <div className="bootstrap-field is-cc-url">
                  <span>{translate(t, 'bootstrap.field_cc_url')}</span>
                  <Tooltip title={initDraft.ccUrl || undefined}>
                    <input
                      className={`input ${!canEdit || initDraft.ccUrl?.trim() ? '' : 'is-danger'}`}
                      value={initDraft.ccUrl || ''}
                      disabled={!canEdit}
                      onChange={(event) => handleInitFieldChange('ccUrl', event.target.value)}
                    />
                  </Tooltip>
                </div>
              )}
              <div className="bootstrap-field is-build">
                <span>{translate(t, 'bootstrap.field_build')}</span>
                <Tooltip title={draftBuild || undefined}>
                  <div className={`select is-fullwidth ${!canEdit || draftBuild ? '' : 'is-danger'}`}>
                    <select
                      value={draftBuild}
                      disabled={!canEdit}
                      onChange={(event) => handleBuildReferenceChange(event.target.value)}
                    >
                      <option value="" />
                      {builds.map((build) => (
                        <option key={build.fileName} value={build.fileName}>
                          {build.fileName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Tooltip>
              </div>
              <AccessControlledComponent module={Modules.bootstrap} role={Roles.SU}>
                <button
                  type="button"
                  className="button is-primary bootstrap-save-button"
                  disabled={!isDirty || !isInitValid || isSaving}
                  onClick={handleSave}
                >
                  {translate(t, 'general.save')}
                </button>
              </AccessControlledComponent>
            </div>
          </div>
        )}
      </section>

      <BuildConfiguration
        builds={builds}
        hasSelection={hasVersionAndPlatform}
        referencedBuild={savedBuild}
        onDeleteBuild={setBuildToDelete}
        onUploadBuild={handleUploadBuild}
        onCancelUpload={handleCancelUpload}
        uploadingFileName={uploadingFileName}
        uploadProgress={uploadProgress}
      />

      {isNewPlatformModalOpen && (
        <NewPlatformModal
          existingPlatforms={platforms}
          onCancel={() => setIsNewPlatformModalOpen(false)}
          onConfirm={handleNewPlatform}
        />
      )}

      {buildToDelete && (
        <DeleteBuildModal
          buildName={buildToDelete.fileName}
          onCancel={() => setBuildToDelete(undefined)}
          onConfirm={handleDeleteBuild}
        />
      )}
    </>
  )
}
